import { buildSimplePrintDocument } from './simple-document';
import { getCompanyLetterhead } from '../company-letterhead';
import type { PrintDocumentModel } from '../types';
import {
  CSS_FORM_CODE,
  CSS_PARAMETER_KEYS,
  CSS_PARAMETER_LABELS,
  CSS_SCORE_OPTIONS,
  type CssParameterKey,
  type CustomerSatisfactionSurvey,
} from '@/constants/customer-satisfaction-survey';
import { formatDate, formatDateTime } from '@/utils/format';

const PROCEDURE = 'NTES-QSP-SAL-001';

export type CustomerSatisfactionPrintBrand = {
  legalName: string;
  trn: string;
};

export type CustomerSatisfactionRegisterPrintFilters = {
  search?: string;
  status?: string;
  result?: string;
  dateFrom?: string;
  dateTo?: string;
};

function dash(value: unknown): string {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
}

function scoreLabel(score: number | null | undefined): string {
  if (score == null) return '—';
  return CSS_SCORE_OPTIONS.find((opt) => opt.value === score)?.label || String(score);
}

function filterLine(filters: CustomerSatisfactionRegisterPrintFilters): string {
  const parts = [
    filters.status ? `Status ${filters.status}` : 'All statuses',
    filters.result ? `Result ${filters.result}` : 'All results',
    filters.dateFrom || filters.dateTo
      ? `Created ${filters.dateFrom || '…'} to ${filters.dateTo || '…'}`
      : 'All dates',
    filters.search ? `Search “${filters.search}”` : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/** One NTES-QSF-SAL-01 record on the shared letterhead page plan. */
export function buildCustomerSatisfactionSurveyPrint(input: {
  survey: CustomerSatisfactionSurvey;
  brand: CustomerSatisfactionPrintBrand;
  printedAt: Date;
}): PrintDocumentModel {
  const { survey, brand, printedAt } = input;
  const scores = survey.parameter_scores;
  const customer = dash(survey.customer_name_snapshot || survey.customer_name);
  const docNo = dash(survey.survey_number);

  return buildSimplePrintDocument({
    docType: 'customer_satisfaction_survey',
    title: 'CUSTOMER SATISFACTION SURVEY',
    subtitle: `${CSS_FORM_CODE} · Procedure ${PROCEDURE}`,
    documentNumber: docNo,
    partyName: customer,
    templateId: 'letter-letterhead',
    letterhead: getCompanyLetterhead(),
    continuationLabel: `${docNo} | ${customer}`,
    partyBlock: {
      leftRows: [
        { label: 'M/S', value: customer },
        { label: 'Address', value: dash(survey.customer_address) },
        { label: 'Attn', value: dash(survey.client_rep_name) },
        { label: 'Subject', value: dash(survey.scope_of_work) },
      ],
      rightRows: [
        { label: 'TRN', value: dash(brand.trn) },
        { label: 'Ref No', value: docNo },
        { label: 'Date', value: formatDate(survey.feedback_date, { nullDisplay: '—' }) },
        { label: 'Project', value: dash(survey.project_or_po_reference) },
        { label: 'Order', value: dash(survey.sales_order_number) },
        { label: 'Status', value: dash(survey.status) },
      ],
    },
    columns: [
      { key: 'sno', label: 'Sr', width: '36px' },
      { key: 'description', label: 'Parameter' },
      { key: 'score', label: 'Score', width: '170px' },
      { key: 'comments', label: 'Comments', width: '190px' },
    ],
    lines: CSS_PARAMETER_KEYS.map((key: CssParameterKey, index) => ({
      lineNumber: index + 1,
      description: CSS_PARAMETER_LABELS[key],
      cells: {
        score: scoreLabel(scores?.[key]?.score),
        comments: dash(scores?.[key]?.comments),
      },
    })),
    termsHeading: 'Result',
    termRows: [
      {
        label: 'Total score (A)',
        value: survey.total_score_a != null ? String(survey.total_score_a) : '—',
      },
      { label: 'Max score (B)', value: String(survey.max_score_b ?? 30) },
      {
        label: 'Satisfaction',
        value:
          survey.satisfaction_pct != null ? `${Number(survey.satisfaction_pct).toFixed(2)}%` : '—',
      },
      {
        label: 'KPI target',
        value: survey.kpi_target_pct != null ? `${survey.kpi_target_pct}%` : '—',
      },
      { label: 'Result', value: dash(survey.satisfaction_result) },
      { label: 'NCR required', value: survey.requires_ncr ? 'Yes' : 'No' },
    ],
    termsExtra: [
      `Overall comments. ${dash(survey.overall_comments)}`,
      survey.sales_notes ? `Sales notes. ${dash(survey.sales_notes)}` : '',
      `Client representative ${dash(survey.client_rep_name)}${
        survey.client_rep_email ? ` · ${survey.client_rep_email}` : ''
      }${survey.client_rep_signature ? ` · Signed ${survey.client_rep_signature}` : ''} · ${formatDate(
        survey.client_rep_date,
        { nullDisplay: '—' }
      )}`,
      `Completed ${formatDateTime(survey.completed_at, { nullDisplay: '—' }) || '—'} · Reviewed ${
        formatDateTime(survey.reviewed_at, { nullDisplay: '—' }) || '—'
      } · Printed ${formatDateTime(printedAt) || '—'}`,
      `Retained record of survey ${docNo}. File with procedure ${PROCEDURE}.${
        survey.requires_ncr ? ' Unsatisfied outcome requires a Non-Conformance Report and CAPA.' : ''
      }`,
    ].filter(Boolean),
  });
}

/** Filtered register on the shared A4 landscape page plan. */
export function buildCustomerSatisfactionRegisterPrint(input: {
  rows: CustomerSatisfactionSurvey[];
  total: number;
  filters: CustomerSatisfactionRegisterPrintFilters;
  brand: CustomerSatisfactionPrintBrand;
  printedAt: Date;
}): PrintDocumentModel {
  const { rows, total, filters, brand, printedAt } = input;
  const recordLabel = `${rows.length}${total > rows.length ? ` of ${total}` : ''}`;

  return buildSimplePrintDocument({
    docType: 'customer_satisfaction_register',
    title: 'CUSTOMER SATISFACTION REGISTER',
    subtitle: `${CSS_FORM_CODE} · Procedure ${PROCEDURE}${brand.legalName ? ` · ${brand.legalName}` : ''}`,
    documentNumber: CSS_FORM_CODE,
    partyName: brand.legalName || 'Company',
    templateId: 'a4-landscape',
    letterhead: false,
    continuationLabel: `${CSS_FORM_CODE} | Customer Satisfaction Register`,
    metaLines: [
      { label: 'Printed', value: formatDateTime(printedAt) || '—' },
      { label: 'Records', value: recordLabel },
      { label: 'Form', value: CSS_FORM_CODE },
      { label: 'Company', value: dash(brand.legalName) },
    ],
    introText: filterLine(filters),
    columns: [
      { key: 'sno', label: 'Sr', width: '36px' },
      { key: 'description', label: 'Survey no', width: '110px' },
      { key: 'date', label: 'Date', width: '88px' },
      { key: 'customer', label: 'Customer' },
      { key: 'project', label: 'Project / PO', width: '120px' },
      { key: 'order', label: 'Sales order', width: '110px' },
      { key: 'status', label: 'Status', width: '80px' },
      { key: 'score', label: 'Score %', align: 'right', width: '64px' },
      { key: 'result', label: 'Result', width: '88px' },
    ],
    lines:
      rows.length === 0
        ? [
            {
              lineNumber: '',
              description: 'No surveys match this register.',
              cells: {},
            },
          ]
        : rows.map((row, index) => ({
            id: row.id,
            lineNumber: index + 1,
            description: dash(row.survey_number),
            cells: {
              date: formatDate(row.feedback_date || row.created_at, { nullDisplay: '—' }),
              customer: dash(row.customer_name_snapshot || row.customer_name),
              project: dash(row.project_or_po_reference),
              order: dash(row.sales_order_number),
              status: dash(row.status),
              score: row.satisfaction_pct != null ? Number(row.satisfaction_pct).toFixed(1) : '—',
              result: dash(row.satisfaction_result),
            },
          })),
    termsExtra: [
      `Retained record of the Customer Satisfaction Register. File with procedure ${PROCEDURE}.`,
    ],
  });
}
